import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Button, Group, Modal, Slider, Text } from '@mantine/core';
import './ImageCropModal.css';

const MAX_ZOOM = 4;
const OUTPUT_MAX_SIDE = 1920;
const OUTPUT_QUALITY = 0.92;

interface ImageCropModalProps {
  file: File | null;
  onClose: () => void;
  onApply: (file: File) => void;
}

type Point = { x: number; y: number };

function croppedName(name: string): string {
  const base = name.replace(/\.[^.]+$/, '');
  return `${base || 'image'}.jpg`;
}

export default function ImageCropModal({ file, onClose, onApply }: ImageCropModalProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ start: Point; origin: Point } | null>(null);

  const view = useMemo(
    () => (typeof window === 'undefined' ? 400 : Math.min(400, window.innerWidth - 64)),
    [file],
  );

  useEffect(() => {
    setNatural(null);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    if (!file) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const baseScale = natural ? view / Math.min(natural.w, natural.h) : 1;
  const scale = baseScale * zoom;
  const displayW = natural ? natural.w * scale : view;
  const displayH = natural ? natural.h * scale : view;

  const clamp = (point: Point, w: number, h: number): Point => ({
    x: Math.min(0, Math.max(view - w, point.x)),
    y: Math.min(0, Math.max(view - h, point.y)),
  });

  const handleLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const s = view / Math.min(w, h);
    setNatural({ w, h });
    setOffset({ x: (view - w * s) / 2, y: (view - h * s) / 2 });
  };

  const handleZoom = (next: number) => {
    if (!natural) return;
    // Центр рамки остаётся на той же точке фото
    const centerX = (view / 2 - offset.x) / scale;
    const centerY = (view / 2 - offset.y) / scale;
    const nextScale = baseScale * next;
    setZoom(next);
    setOffset(
      clamp(
        { x: view / 2 - centerX * nextScale, y: view / 2 - centerY * nextScale },
        natural.w * nextScale,
        natural.h * nextScale,
      ),
    );
  };

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!natural) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { start: { x: e.clientX, y: e.clientY }, origin: offset };
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    setOffset(
      clamp(
        {
          x: drag.origin.x + e.clientX - drag.start.x,
          y: drag.origin.y + e.clientY - drag.start.y,
        },
        displayW,
        displayH,
      ),
    );
  };

  const handlePointerUp = () => {
    dragRef.current = null;
  };

  const handleApply = async () => {
    const img = imgRef.current;
    if (!file || !img || !natural) return;
    setSaving(true);
    try {
      const sourceX = -offset.x / scale;
      const sourceY = -offset.y / scale;
      const sourceSide = view / scale;
      const outSide = Math.max(1, Math.min(OUTPUT_MAX_SIDE, Math.round(sourceSide)));

      const canvas = document.createElement('canvas');
      canvas.width = outSide;
      canvas.height = outSide;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, sourceX, sourceY, sourceSide, sourceSide, 0, 0, outSide, outSide);

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', OUTPUT_QUALITY),
      );
      if (!blob) return;
      onApply(
        new File([blob], croppedName(file.name), { type: 'image/jpeg', lastModified: Date.now() }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={!!file}
      onClose={onClose}
      title="Квадратная область фото"
      centered
      size="auto"
      zIndex={2000}
    >
      <div
        className="image-crop__viewport"
        style={{ width: view, height: view }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {url ? (
          <img
            ref={imgRef}
            src={url}
            alt=""
            draggable={false}
            onLoad={handleLoad}
            className="image-crop__image"
            style={{
              width: displayW,
              height: displayH,
              transform: `translate(${offset.x}px, ${offset.y}px)`,
              visibility: natural ? 'visible' : 'hidden',
            }}
          />
        ) : null}
      </div>
      <Text size="sm" c="dimmed" mt="sm" maw={view}>
        Перетащите фото, чтобы выбрать область. Именно так оно будет показано в каталоге и на карточке товара.
      </Text>
      <Slider
        mt="md"
        min={1}
        max={MAX_ZOOM}
        step={0.01}
        value={zoom}
        onChange={handleZoom}
        label={null}
        disabled={!natural}
      />
      <Group justify="flex-end" mt="lg">
        <Button variant="default" onClick={onClose}>
          Отмена
        </Button>
        <Button onClick={() => void handleApply()} loading={saving} disabled={!natural}>
          Применить
        </Button>
      </Group>
    </Modal>
  );
}
